import React, { useEffect, useState } from 'react'
import { TextField } from 'src/Libs/inputs/formik'
import { useFormikContext } from 'formik'
import { Typography, Box, Stack } from '@mui/material'
import { makeStyles } from 'tss-react/mui'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { useSnackbar } from '@e3dc-react/shell/Hooks/Snackbar'
import { AddressFormData, PartnerNumberFormFields } from 'src/Libs/installation-statement/types'
import { getCompanyNameUsingPartnerNumber } from 'src/Libs/installation-statement/api'
import { useAuthFetchWithResponse } from '@e3dc-react/shell/Hooks/AuthFetch'
import { PARTNER_NUMBER_FIELD_LENGTH } from 'src/Libs/installation-statement/hooks/validation'

const useStyles = makeStyles()(theme => ({
  partnerNumber: {
    gap: theme.spacing(2),
    flexDirection: 'column',
    justifyContent: 'center',

    [theme.breakpoints.up('sm')]: {
      flexDirection: 'row',
      justifyContent: 'flex-start',
      alignItems: 'center',
    },
  },
}))

/**
 * Component to provide partner number show partner company name in the installation statement address form.
 */
const PartnerNumberSection: React.FC = () => {
  const { t } = useCustomTranslation()
  const { classes } = useStyles()
  const { enqueueErrorStack } = useSnackbar()
  const {
    values: { partnerNumber, partnerNumberEnabled },
    setFieldValue,
  } = useFormikContext<AddressFormData>()
  const [partnerCompanyName, setPartnerCompanyName] = useState<string | null>(null)

  const authFetch = useAuthFetchWithResponse()

  const shouldFetchPartnerCompanyName = partnerNumberEnabled && partnerNumber?.length === PARTNER_NUMBER_FIELD_LENGTH

  useEffect(() => {
    if (!shouldFetchPartnerCompanyName) {
      setPartnerCompanyName(null)
      setFieldValue(PartnerNumberFormFields.PARTNER_NUMBER_VALID, false)
      return
    }

    const fetchPartnerCompanyName = async (): Promise<void> => {
      const { value, hasErrors } = await getCompanyNameUsingPartnerNumber(Number(partnerNumber), authFetch)

      if (hasErrors) {
        enqueueErrorStack(t('errors.installation-statement.partnerNumberError'))
      }

      if (value && value.Name.length !== 0) {
        setPartnerCompanyName(value.Name)
        setFieldValue(PartnerNumberFormFields.PARTNER_NUMBER_VALID, true)
        return
      }

      setPartnerCompanyName(null)
      setFieldValue(PartnerNumberFormFields.PARTNER_NUMBER_VALID, false)
    }

    fetchPartnerCompanyName()

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldFetchPartnerCompanyName, partnerNumber])

  return (
    <Stack className={classes.partnerNumber}>
      <Box>
        <TextField
          name={PartnerNumberFormFields.PARTNER_NUMBER}
          label={t('installation-statement.address.installation-for.partner-number')}
          textAlign="left"
          required
          mask="#### #### ####"
        />
      </Box>
      {partnerCompanyName && <Typography variant="body2">{partnerCompanyName}</Typography>}
    </Stack>
  )
}

export default PartnerNumberSection
